import {
  doc,
  setDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import { handleAuthError } from './authRecoveryService';
import type {
  Entity,
  CreateEntityInput,
  UpdateEntityInput,
} from '../types/settings';

// ============================================================================
// ENTITY CRUD
// ============================================================================

/**
 * Create a new entity
 */
export async function createEntity(
  orgId: string,
  input: CreateEntityInput
): Promise<string> {
  const db = getFirebaseDb();
  const entityRef = doc(collection(db, 'organizations', orgId, 'entities'));
  const entityId = entityRef.id;

  await setDoc(entityRef, {
    id: entityId,
    name: input.name,
    isActive: true,
    createdAt: serverTimestamp(),
  });

  return entityId;
}

/**
 * Get all entities for an organization
 */
export async function getEntities(orgId: string): Promise<Entity[]> {
  const db = getFirebaseDb();
  const entitiesRef = collection(db, 'organizations', orgId, 'entities');
  const q = query(entitiesRef, orderBy('name', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => doc.data() as Entity);
}

/**
 * Get active entities only
 */
export async function getActiveEntities(orgId: string): Promise<Entity[]> {
  const db = getFirebaseDb();
  const entitiesRef = collection(db, 'organizations', orgId, 'entities');
  const q = query(entitiesRef, where('isActive', '==', true), orderBy('name', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => doc.data() as Entity);
}

/**
 * Subscribe to entities (real-time updates)
 */
export function subscribeEntities(
  orgId: string,
  callback: (entities: Entity[]) => void
): () => void {
  const db = getFirebaseDb();
  const entitiesRef = collection(db, 'organizations', orgId, 'entities');
  const q = query(entitiesRef, orderBy('name', 'asc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const entities = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Entity[];
      callback(entities);
    },
    async (error) => {
      console.error('[EntityService] Subscription error:', error);
      await handleAuthError(error);
    }
  );
}

/**
 * Update an entity
 */
export async function updateEntity(
  orgId: string,
  entityId: string,
  input: UpdateEntityInput
): Promise<void> {
  const db = getFirebaseDb();
  await updateDoc(doc(db, 'organizations', orgId, 'entities', entityId), {
    ...input,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete an entity
 */
export async function deleteEntity(
  orgId: string,
  entityId: string
): Promise<void> {
  const db = getFirebaseDb();
  await deleteDoc(doc(db, 'organizations', orgId, 'entities', entityId));
}

/**
 * Toggle entity active status
 */
export async function toggleEntityActive(
  orgId: string,
  entityId: string,
  isActive: boolean
): Promise<void> {
  const db = getFirebaseDb();
  await updateDoc(doc(db, 'organizations', orgId, 'entities', entityId), {
    isActive,
    updatedAt: serverTimestamp(),
  });
}
